/**
 * client/game/killcam.ts — killcam replay (respawn killcam + final killcam). OWNER: integration.
 *
 * BO2 replays a kill from the attacker's first-person view: the victim's client spectates the attacker with
 * `archivetime` = camtime + predelay, i.e. it watches archived snapshots of server time
 * [deathTime - camtime, deathTime + postdelay] (_killcam.gsc:172-260 killcam(), 470-556 finalkillcam()).
 *
 * Our server does not archive snapshots per client, so the CLIENT archives what it already received: every render
 * frame it records the interpolated remote players, the local player (from its predicted state), the missiles and
 * the frame's events on the server timeline (renderTime). When the gametype's KILLCAM event arrives (#G1: the
 * gametype owns the timing, sas.ts) the app swaps in a ClientWorld built from that recording, as seen by the
 * attacker, for the renderer, viewmodel and audio, and a RefDef at the attacker's eye. The HUD keeps the live world
 * (it draws the KILLCAM letterbox from the same event).
 *
 *  - Respawn killcam: footage [deathTime - camMs, deathTime + postMs] played in real time; ends early when the
 *    local player spawns (skip, _killcam.gsc waitskipkillcambutton) or the match leaves PLAYING (level endon
 *    "game_ended").
 *  - Final killcam: the clip of the last non-suicide kill (copied out of the ring once its post-delay was
 *    recorded), played with the waitfinalkillcamslowdown slow motion (killcam-time.ts).
 *
 * ESTIMATEs (engine behaviour not in the scripts):
 *  - Remote players are archived at the client's interpolation rate (20 Hz snapshots, lerped), not at the
 *    attacker's 60 Hz usercmd rate, so the attacker's view turns a little smoother than retail.
 *  - No view bob / fire kick in the replayed view; ADS fraction is lerped from the networked ADS bit over the
 *    weapon's adsTransIn/OutTime.
 *  - The attacker's viewmodel anims are rebuilt from his networked events (fire, reload, raise, melee, sprint);
 *    ammo is not networked, so empty-clip idles are not shown.
 *  - Killcam-entity views (setkillcamentity: camera following the bolt / axe / ballistic knife after it was
 *    launched, getkillcamentity returns the inflictor) are NOT replayed: the camera stays on the attacker.
 */
import { lerpAngle, type Vec3 } from '@shared/math';
import { createPlayerState, psStance } from '@shared/playerstate';
import {
  EF, GameEventType, MatchPhase, MoveFlag, PEv, PMF, PmType, Stance, WEAPANIM_TOGGLEBIT, WeapAnim, WeaponId, WeaponState,
  type GameEvent, type PlayerState,
} from '@shared/types';
import { ENTITYNUM_NONE, ENTITYNUM_WORLD } from '@shared/constants';
import { getWeaponDef } from '@shared/weapons/defs';
import { cgFovToVertical, updatePositionToAds, viewFov } from '../camera/fov';
import type { Settings } from '../settings/settings';
import type { ClientWorld, FrameEvent, LocalView, NetStats, RefDef, RenderMissile, RenderPlayer } from '../types';
import { finalKillcamGameTime, finalKillcamRealDuration } from './killcam-time';

/** Ring length of the live archive (ms of server time). Respawn killcam needs camMs + postMs + death watch 2 s. */
export const ARCHIVE_MS = 10_000;
/** Record at most one frame per this many ms of server time (60 Hz like a usercmd stream). */
export const ARCHIVE_STEP_MS = 1000 / 60;
/** Final killcam footage (sim/gametype/rules.ts FINAL_KILLCAM_CAM_MS / _POST_MS, _killcam.gsc calckillcamtime). */
export const FINAL_CAM_MS = 5000;
export const FINAL_POST_MS = 2000;
/** spawnCount offset for replayed players, so renderer/viewmodel reset their anims entering and leaving a replay. */
export const REPLAY_SPAWN_OFFSET = 0x40000000;

/** Game event types replayed from the footage (world presentation). The rest (HUD / scoring) stay live. */
const REPLAY_GAME_EVENTS = new Set<number>([
  GameEventType.EXPLOSION, GameEventType.IMPACT, GameEventType.MISSILE_STICK, GameEventType.MISSILE_BOUNCE,
  GameEventType.MELEE_HIT, GameEventType.PAIN, GameEventType.DEATH, GameEventType.SPAWN, GameEventType.PICKUP,
  GameEventType.SOUND, GameEventType.SPLASH,
]);
/** Live game events still delivered to the replay world (announcer, medals, music state, scoreboard). */
const LIVE_GAME_EVENTS = new Set<number>([
  GameEventType.ANNOUNCER, GameEventType.MEDAL, GameEventType.BANKRUPT, GameEventType.TOP3, GameEventType.MATCH,
  GameEventType.SCORE, GameEventType.SCORE_POPUP,
]);

/** One archived player: a RenderPlayer plus what the local player's own record knows exactly. */
export interface RecPlayer extends RenderPlayer {
  /** ps.weaponAnim for the local player's own record, -1 for remote players (rebuilt from events). */
  weaponAnim: number;
  /** ps.adsFrac for the local record, -1 for remote players (from MoveFlag.ADS). */
  adsFrac: number;
}

export interface RecFrame {
  /** Server time (the client's renderTime when recorded). */
  t: number;
  players: RecPlayer[];
  nPlayers: number;
  missiles: RenderMissile[];
  nMissiles: number;
  /** Events delivered this frame; local predicted events are stored as 'ent' of the local client. */
  events: FrameEvent[];
}

function blankRecPlayer(): RecPlayer {
  return {
    clientNum: 0, info: { clientNum: 0, name: '', isBot: false, body: 0, ping: 0, ready: false }, origin: [0, 0, 0], angles: [0, 0, 0],
    velocity: [0, 0, 0], stance: Stance.STAND, viewHeight: 0, moveFlags: 0, eFlags: 0, weapon: WeaponId.NONE,
    weaponState: WeaponState.READY, legsAnim: 0, torsoAnim: 0, spawnCount: 0, alive: false, weaponAnim: -1, adsFrac: -1,
  };
}
function blankMissile(): RenderMissile {
  return { entId: 0, kind: 0 as RenderMissile['kind'], owner: 0, state: 0 as RenderMissile['state'], origin: [0, 0, 0], angles: [0, 0, 0], velocity: [0, 0, 0], explodeAt: 0, predicted: false };
}
function newFrame(): RecFrame { return { t: 0, players: [], nPlayers: 0, missiles: [], nMissiles: 0, events: [] }; }

function copy3(d: Vec3, s: Readonly<Vec3>): void { d[0] = s[0]; d[1] = s[1]; d[2] = s[2]; }
function copyPlayer(d: RecPlayer, s: RenderPlayer, weaponAnim = -1, adsFrac = -1): void {
  d.clientNum = s.clientNum; d.info = s.info;
  copy3(d.origin, s.origin); copy3(d.angles, s.angles); copy3(d.velocity, s.velocity);
  d.stance = s.stance; d.viewHeight = s.viewHeight; d.moveFlags = s.moveFlags; d.eFlags = s.eFlags; d.weapon = s.weapon;
  d.weaponState = s.weaponState; d.legsAnim = s.legsAnim; d.torsoAnim = s.torsoAnim; d.spawnCount = s.spawnCount; d.alive = s.alive;
  d.weaponAnim = weaponAnim; d.adsFrac = adsFrac;
}
function copyMissile(d: RenderMissile, s: RenderMissile): void {
  d.entId = s.entId; d.kind = s.kind; d.owner = s.owner; d.state = s.state; copy3(d.origin, s.origin); copy3(d.angles, s.angles);
  copy3(d.velocity, s.velocity); d.explodeAt = s.explodeAt; d.predicted = s.predicted;
}
function cloneFrame(f: RecFrame): RecFrame {
  const o = newFrame();
  o.t = f.t;
  for (let i = 0; i < f.nPlayers; i++) { const p = blankRecPlayer(); copyPlayer(p, f.players[i]!, f.players[i]!.weaponAnim, f.players[i]!.adsFrac); o.players.push(p); }
  o.nPlayers = f.nPlayers;
  for (let i = 0; i < f.nMissiles; i++) { const m = blankMissile(); copyMissile(m, f.missiles[i]!); o.missiles.push(m); }
  o.nMissiles = f.nMissiles;
  o.events = f.events.slice();
  return o;
}

/**
 * The local player's third-person state from its predicted PlayerState (the server never sends a client its own
 * entity). Mirrors sim/core/game.ts playerEntityState (moveFlags bits), at render rate (world.view).
 */
export function localRenderPlayer(world: ClientWorld, out: RecPlayer): RecPlayer {
  const ps = world.predicted;
  const v = world.view;
  const dead = ps.pm_type === PmType.DEAD || ps.pm_type === PmType.DEAD_LINKED || (ps.eFlags & EF.DEAD) !== 0;
  let mf = 0;
  if (ps.groundEntityNum !== ENTITYNUM_NONE) mf |= MoveFlag.ON_GROUND;
  if (ps.pm_flags & PMF.SPRINTING) mf |= MoveFlag.SPRINTING;
  if (ps.adsFrac > 0.5) mf |= MoveFlag.ADS;
  if (ps.pm_flags & PMF.MANTLE) mf |= MoveFlag.MANTLING;
  if (ps.meleeChargeTime > 0 && ps.meleeChargeDist > 0) mf |= MoveFlag.MELEE_LUNGE;
  if (ps.pm_flags & PMF.BACKWARDS_RUN) mf |= MoveFlag.BACKWARDS;
  if (ps.pm_flags & PMF.DIVE) mf |= MoveFlag.DIVING;
  if (dead) mf |= MoveFlag.DEAD;
  out.clientNum = world.localClientNum;
  out.info = world.playerInfo.get(world.localClientNum) ?? out.info;
  copy3(out.origin, v.origin); copy3(out.angles, v.viewangles); copy3(out.velocity, v.velocity);
  out.stance = psStance(ps); out.viewHeight = v.viewHeight; out.moveFlags = mf; out.eFlags = ps.eFlags & 0xffff;
  out.weapon = ps.weapon; out.weaponState = ps.weaponState; out.legsAnim = 0; out.torsoAnim = 0;
  out.spawnCount = ps.spawnCount; out.alive = !dead;
  out.weaponAnim = ps.weaponAnim; out.adsFrac = ps.adsFrac;
  return out;
}

// =============================================================================================
// Archive: a ring of recorded frames (pooled, no per-frame allocation once warm)
// =============================================================================================
export interface FrameList {
  readonly length: number;
  get(i: number): RecFrame;
}

export class KillcamArchive implements FrameList {
  private readonly ring: RecFrame[];
  private head = 0; // index of the oldest frame
  length = 0;
  constructor(readonly capacity = Math.ceil(ARCHIVE_MS / ARCHIVE_STEP_MS) + 8) {
    this.ring = Array.from({ length: capacity }, newFrame);
  }
  get(i: number): RecFrame { return this.ring[(this.head + i) % this.capacity]!; }
  get newest(): RecFrame | null { return this.length ? this.get(this.length - 1) : null; }
  get oldest(): RecFrame | null { return this.length ? this.get(0) : null; }
  clear(): void { this.head = 0; this.length = 0; }

  /** Record this frame of the live world (call once per render frame after session.frame). */
  record(world: ClientWorld): void {
    const t = world.renderTime;
    if (!(t > 0)) return;
    const last = this.newest;
    if (last && t < last.t - 1000) this.clear(); // the clock was reset / new match: start over
    const lastNow = this.newest;
    // too soon after the previous record: keep its state, but never drop events
    if (lastNow && t - lastNow.t < ARCHIVE_STEP_MS - 0.5) { this.addEvents(lastNow, world); return; }
    let f: RecFrame;
    if (this.length < this.capacity) f = this.get(this.length++);
    else { f = this.ring[this.head]!; this.head = (this.head + 1) % this.capacity; }
    f.t = t;
    let n = 0;
    for (const p of world.players) {
      const d = f.players[n] ?? (f.players[n] = blankRecPlayer());
      copyPlayer(d, p);
      n++;
    }
    if (world.localClientNum >= 0 && world.predicted.spawnCount > 0) {
      const d = f.players[n] ?? (f.players[n] = blankRecPlayer());
      localRenderPlayer(world, d);
      n++;
    }
    f.nPlayers = n;
    let m = 0;
    for (const s of world.missiles) {
      const d = f.missiles[m] ?? (f.missiles[m] = blankMissile());
      copyMissile(d, s);
      m++;
    }
    f.nMissiles = m;
    f.events.length = 0;
    this.addEvents(f, world);
  }

  private addEvents(f: RecFrame, world: ClientWorld): void {
    for (const e of world.frameEvents) {
      if (e.src === 'pred') f.events.push({ src: 'ent', ent: world.localClientNum, ev: e.ev, parm: e.parm });
      else if (e.src === 'ent' || REPLAY_GAME_EVENTS.has(e.ge.type)) f.events.push(e);
    }
  }

  /** Deep copy of the frames with t in [t0, t1] (the final killcam clip). */
  copyRange(t0: number, t1: number): RecFrame[] {
    const out: RecFrame[] = [];
    for (let i = 0; i < this.length; i++) {
      const f = this.get(i);
      if (f.t >= t0 && f.t <= t1) out.push(cloneFrame(f));
    }
    return out;
  }
}

class ArrayFrames implements FrameList {
  constructor(private readonly a: RecFrame[]) {}
  get length(): number { return this.a.length; }
  get(i: number): RecFrame { return this.a[i]!; }
}

/** Index of the last frame with t <= time (-1 if none). */
export function frameIndexAt(frames: FrameList, time: number): number {
  let lo = 0, hi = frames.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames.get(mid).t <= time) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
}

// =============================================================================================
// Replay world (what the renderer / viewmodel / audio see during a killcam)
// =============================================================================================
class ReplayWorld implements ClientWorld {
  connected = true;
  localClientNum = -1;
  map: ClientWorld['map'] = null;
  renderTime = 0;
  predicted: PlayerState = createPlayerState();
  view: LocalView = { origin: [0, 0, 0], viewangles: [0, 0, 0], viewHeight: 60, velocity: [0, 0, 0], alpha: 0 };
  players: RenderPlayer[] = [];
  missiles: RenderMissile[] = [];
  playerInfo: ReadonlyMap<number, RenderPlayer['info']> = new Map();
  scoreboard: ClientWorld['scoreboard'] = [];
  phase: MatchPhase = MatchPhase.PLAYING;
  timeLeftMs = 0;
  timeLimitMs = 0;
  stats!: NetStats;
  frameEvents: FrameEvent[] = [];
  getPlayer(clientNum: number): RenderPlayer | undefined {
    for (const p of this.players) if (p.clientNum === clientNum) return p;
    return undefined;
  }
}

export interface KillcamInfo {
  attacker: number;
  victim: number;
  weapon: WeaponId;
  deathTime: number;
  camMs: number;
  postMs: number;
  final: boolean;
}

interface Playback extends KillcamInfo {
  frames: FrameList;
  startReal: number;
  /** Footage start (server ms). */
  t0: number;
  /** Footage end (server ms). */
  t1: number;
  /** Real ms the playback lasts (final: slow motion). */
  realMs: number;
  /** Last footage time delivered (events in (lastT, t] play this frame). */
  lastT: number;
  /** Synthesized attacker viewmodel state. */
  lastWeaponState: number;
  ads: number;
  positionToAds: boolean;
  lastFrameReal: number;
}

/** Real ms -> footage ms offset for a playback. */
export function footageOffsetMs(final: boolean, realElapsedMs: number, camMs: number): number {
  if (!final) return realElapsedMs;
  return finalKillcamGameTime(realElapsedMs / 1000, camMs / 1000) * 1000;
}

/** Final killcam real length (ms) for camMs + postMs of footage (killcamlength = camtime + postdelay - 0.05). */
export function finalKillcamRealMs(camMs: number, postMs: number): number {
  return finalKillcamRealDuration((camMs + postMs) / 1000 - 0.05, camMs / 1000) * 1000;
}

export class Killcam {
  readonly archive = new KillcamArchive();
  private readonly world = new ReplayWorld();
  private play: Playback | null = null;
  /** Kill waiting for its post-delay to be recorded before the clip is copied out. */
  private pendingClip: { deathTime: number } | null = null;
  private clip: { deathTime: number; frames: RecFrame[] } | null = null;
  private readonly refdef: RefDef = { origin: [0, 0, 0], angles: [0, 0, 0], fovY: 51.08, viewmodelFovY: 51.08, zNear: 4, zFar: 1e5, adsFrac: 0 };
  /** Counters for tests / window.__sns. */
  readonly stats = { started: 0, finalStarted: 0, ended: 0, noFootage: 0, lastReason: '' };

  get active(): boolean { return this.play !== null; }
  get final(): boolean { return !!this.play?.final; }
  get info(): KillcamInfo | null { return this.play; }
  /** The replay world while a killcam plays (debug / tests). */
  get replay(): ClientWorld | null { return this.play ? this.world : null; }

  reset(): void {
    this.archive.clear();
    this.play = null;
    this.pendingClip = null;
    this.clip = null;
  }

  /**
   * Once per render frame after session.frame: archive the live frame, react to KILL / KILLCAM / SPAWN / MATCH
   * events, and advance an active playback. Returns the replay world while a killcam plays, else null.
   */
  update(live: ClientWorld, realNow: number): ClientWorld | null {
    this.archive.record(live);
    const me = live.localClientNum;
    for (const e of live.frameEvents) {
      if (e.src !== 'game') continue;
      const ge = e.ge;
      if (ge.type === GameEventType.KILL && ge.killer >= 0 && ge.killer !== ge.victim) {
        this.pendingClip = { deathTime: ge.time }; // recordkillcamsettings: every non-suicide player kill (_killcam.gsc:53-82)
      } else if (ge.type === GameEventType.KILLCAM) {
        if (ge.final) this.startFinal(ge, realNow);
        else if (ge.victim === me) this.startRespawn(ge, realNow);
      } else if (ge.type === GameEventType.SPAWN && ge.ent === me && this.play && !this.play.final) {
        this.stop('spawned'); // _killcam.gsc killcam(): self endon("spawned")
      }
    }
    // copy the last kill's clip once its post-delay is archived (the final killcam may come much later)
    const newest = this.archive.newest;
    if (this.pendingClip && newest && newest.t >= this.pendingClip.deathTime + FINAL_POST_MS + 100) {
      const d = this.pendingClip.deathTime;
      this.clip = { deathTime: d, frames: this.archive.copyRange(d - FINAL_CAM_MS - 200, d + FINAL_POST_MS + 100) };
      this.pendingClip = null;
    }
    const p = this.play;
    if (!p) return null;
    // level endon("game_ended"): a respawn killcam stops when the match leaves PLAYING
    if (!p.final && live.phase !== MatchPhase.PLAYING) { this.stop('game_ended'); return null; }
    const elapsed = realNow - p.startReal;
    if (elapsed >= p.realMs) { this.stop('done'); return null; }
    const t = Math.min(p.t1, p.t0 + footageOffsetMs(p.final, elapsed, p.camMs));
    const dtReal = Math.max(0, realNow - p.lastFrameReal);
    p.lastFrameReal = realNow;
    if (!this.build(p, live, t, dtReal)) { this.stop('no footage'); return null; }
    return this.world;
  }

  /** Killcam view (attacker's eye) built on the live camera's projection settings. */
  view(live: RefDef, settings: Settings): RefDef {
    const r = this.refdef;
    const w = this.world;
    r.origin[0] = w.view.origin[0]; r.origin[1] = w.view.origin[1]; r.origin[2] = w.view.origin[2] + w.view.viewHeight;
    r.angles[0] = w.view.viewangles[0]; r.angles[1] = w.view.viewangles[1]; r.angles[2] = 0;
    const p = this.play;
    const wid = w.predicted.weapon;
    const def = wid !== WeaponId.NONE ? getWeaponDef(wid) : null;
    const ads = p ? p.ads : 0;
    if (p) p.positionToAds = updatePositionToAds(p.positionToAds, ads);
    r.fovY = cgFovToVertical(viewFov(settings.fov, def, ads, p ? p.positionToAds : true)); // CG_GetViewFov, the viewer's cg_fov
    r.viewmodelFovY = r.fovY;
    r.zNear = live.zNear; r.zFar = live.zFar;
    r.adsFrac = ads;
    return r;
  }

  /** The match-end screen waits for a final killcam still playing (real ms left, 0 if none). */
  finalRemainingMs(realNow: number): number {
    const p = this.play;
    return p && p.final ? Math.max(0, p.realMs - (realNow - p.startReal)) : 0;
  }

  stop(reason: string): void {
    if (!this.play) return;
    this.play = null;
    this.stats.ended++;
    this.stats.lastReason = reason;
  }

  // ---------------------------------------------------------------------------------------------
  private startRespawn(ge: Extract<GameEvent, { type: typeof GameEventType.KILLCAM }>, realNow: number): void {
    if (ge.attacker < 0 || ge.attacker === ge.victim) return;
    const t0 = ge.deathTime - ge.camMs;
    if (!this.covers(this.archive, ge.attacker, t0, ge.deathTime)) { this.stats.noFootage++; return; }
    this.begin(ge, this.archive, realNow, ge.camMs + ge.postMs);
    this.stats.started++;
  }

  private startFinal(ge: Extract<GameEvent, { type: typeof GameEventType.KILLCAM }>, realNow: number): void {
    if (ge.attacker < 0) return;
    let frames: FrameList | null = null;
    const c = this.clip;
    if (c && Math.abs(c.deathTime - ge.deathTime) <= 250) frames = new ArrayFrames(c.frames);
    else if (this.covers(this.archive, ge.attacker, ge.deathTime - ge.camMs, ge.deathTime)) frames = this.archive;
    else if (this.pendingClip && Math.abs(this.pendingClip.deathTime - ge.deathTime) <= 250) {
      // kill in the last moments: post-delay not archived yet, copy what there is
      frames = new ArrayFrames(this.archive.copyRange(ge.deathTime - ge.camMs - 200, ge.deathTime + ge.postMs + 100));
    }
    if (!frames || !this.covers(frames, ge.attacker, ge.deathTime - ge.camMs, ge.deathTime)) { this.stats.noFootage++; return; }
    this.begin(ge, frames, realNow, finalKillcamRealMs(ge.camMs, ge.postMs));
    this.stats.finalStarted++;
  }

  private begin(ge: Extract<GameEvent, { type: typeof GameEventType.KILLCAM }>, frames: FrameList, realNow: number, realMs: number): void {
    const t0 = Math.max(ge.deathTime - ge.camMs, frames.get(0).t);
    this.play = {
      attacker: ge.attacker, victim: ge.victim, weapon: ge.weapon, deathTime: ge.deathTime, camMs: ge.camMs, postMs: ge.postMs,
      final: ge.final, frames, startReal: realNow, t0, t1: ge.deathTime + ge.postMs, realMs, lastT: t0 - 0.001,
      lastWeaponState: -1, ads: 0, positionToAds: true, lastFrameReal: realNow,
    };
    const ps = this.world.predicted;
    ps.weaponAnim = 0;
    ps.spawnCount = REPLAY_SPAWN_OFFSET + ((ps.spawnCount + 1) & 0xffff); // a "new spawn" for the viewmodel
  }

  /** The attacker exists in the footage at both ends of [t0, t1] (allow the oldest frame to start a bit late). */
  private covers(frames: FrameList, attacker: number, t0: number, t1: number): boolean {
    if (frames.length < 2) return false;
    if (frames.get(0).t > t0 + 500 || frames.get(frames.length - 1).t < t1 - 100) return false;
    const i = frameIndexAt(frames, t1);
    if (i < 0) return false;
    const f = frames.get(i);
    for (let k = 0; k < f.nPlayers; k++) if (f.players[k]!.clientNum === attacker) return true;
    return false;
  }

  private readonly outPlayers: RenderPlayer[] = [];
  private readonly outMissiles: RenderMissile[] = [];

  /** Fill the replay world at footage time t. */
  private build(p: Playback, live: ClientWorld, t: number, dtRealMs: number): boolean {
    const fr = p.frames;
    let i = frameIndexAt(fr, t);
    if (i < 0) i = 0;
    const a = fr.get(i);
    const b = i + 1 < fr.length ? fr.get(i + 1) : a;
    const f = b.t > a.t ? Math.min(1, Math.max(0, (t - a.t) / (b.t - a.t))) : 0;
    const w = this.world;
    // live, world-independent state
    w.connected = live.connected; w.map = live.map; w.playerInfo = live.playerInfo; w.scoreboard = live.scoreboard;
    w.phase = p.final ? MatchPhase.FINAL_KILLCAM : live.phase; w.timeLeftMs = live.timeLeftMs; w.timeLimitMs = live.timeLimitMs ?? 0;
    w.stats = live.stats;
    w.renderTime = t;
    w.localClientNum = p.attacker;
    // players
    let n = 0;
    let attacker: RecPlayer | null = null;
    for (let k = 0; k < a.nPlayers; k++) {
      const pa = a.players[k]!;
      let pb: RecPlayer | null = null;
      for (let j = 0; j < b.nPlayers; j++) if (b.players[j]!.clientNum === pa.clientNum) { pb = b.players[j]!; break; }
      const o = (this.outPlayers[n] ?? (this.outPlayers[n] = blankRecPlayer())) as RecPlayer;
      n++;
      if (pb && pb.spawnCount !== pa.spawnCount) copyPlayer(o, pb, pb.weaponAnim, pb.adsFrac);
      else {
        copyPlayer(o, pa, pa.weaponAnim, pa.adsFrac);
        if (pb) {
          for (let c = 0; c < 3; c++) {
            o.origin[c] = pa.origin[c] + (pb.origin[c] - pa.origin[c]) * f;
            o.angles[c] = lerpAngle(pa.angles[c], pb.angles[c], f);
            o.velocity[c] = pa.velocity[c] + (pb.velocity[c] - pa.velocity[c]) * f;
          }
          o.viewHeight = pa.viewHeight + (pb.viewHeight - pa.viewHeight) * f;
        }
      }
      o.spawnCount = REPLAY_SPAWN_OFFSET + (o.spawnCount & 0xffff);
      if (o.clientNum === p.attacker) attacker = o;
    }
    this.outPlayers.length = n;
    w.players = this.outPlayers;
    if (!attacker) return false;
    // missiles (lerp by entId)
    let m = 0;
    for (let k = 0; k < a.nMissiles; k++) {
      const ma = a.missiles[k]!;
      let mb: RenderMissile | null = null;
      for (let j = 0; j < b.nMissiles; j++) if (b.missiles[j]!.entId === ma.entId) { mb = b.missiles[j]!; break; }
      const o = this.outMissiles[m] ?? (this.outMissiles[m] = blankMissile());
      m++;
      copyMissile(o, ma);
      if (mb) for (let c = 0; c < 3; c++) { o.origin[c] = ma.origin[c] + (mb.origin[c] - ma.origin[c]) * f; o.angles[c] = lerpAngle(ma.angles[c], mb.angles[c], f); }
    }
    this.outMissiles.length = m;
    w.missiles = this.outMissiles;
    // view = attacker's eye
    copy3(w.view.origin, attacker.origin); copy3(w.view.viewangles, attacker.angles); copy3(w.view.velocity, attacker.velocity);
    w.view.viewHeight = attacker.viewHeight; w.view.alpha = 0;
    // events in (lastT, t]
    const ev = w.frameEvents;
    ev.length = 0;
    if (t > p.lastT) {
      for (let k = Math.max(0, frameIndexAt(fr, p.lastT)); k < fr.length; k++) {
        const fk = fr.get(k);
        if (fk.t > t) break;
        if (fk.t <= p.lastT) continue;
        for (const e of fk.events) {
          if (e.src === 'ent' && e.ent === p.attacker) ev.push({ src: 'pred', ent: p.attacker, ev: e.ev, parm: e.parm });
          else ev.push(e);
        }
      }
      p.lastT = t;
    }
    for (const e of live.frameEvents) if (e.src === 'game' && LIVE_GAME_EVENTS.has(e.ge.type)) ev.push(e);
    this.synthPs(p, attacker, t, dtRealMs);
    return true;
  }

  /** The attacker's PlayerState as far as the viewmodel needs it (weapon, state, anim, ADS). */
  private synthPs(p: Playback, at: RecPlayer, t: number, dtRealMs: number): void {
    const ps = this.world.predicted;
    ps.clientNum = at.clientNum;
    ps.commandTime = Math.floor(t);
    ps.pm_type = at.alive ? PmType.NORMAL : PmType.DEAD;
    ps.eFlags = at.alive ? at.eFlags & ~EF.DEAD : at.eFlags | EF.DEAD;
    ps.pm_flags = (at.stance === Stance.PRONE ? PMF.PRONE : at.stance === Stance.CROUCH ? PMF.DUCKED : 0)
      | (at.moveFlags & MoveFlag.SPRINTING ? PMF.SPRINTING : 0) | (at.moveFlags & MoveFlag.MANTLING ? PMF.MANTLE : 0);
    ps.groundEntityNum = at.moveFlags & MoveFlag.ON_GROUND ? ENTITYNUM_WORLD : ENTITYNUM_NONE;
    copy3(ps.origin, at.origin); copy3(ps.viewangles, at.angles); copy3(ps.velocity, at.velocity);
    ps.viewHeightCurrent = at.viewHeight; ps.viewHeightTarget = at.viewHeight;
    ps.health = at.alive ? ps.maxHealth : 0;
    ps.weapon = at.weapon;
    ps.weaponState = at.weaponState;
    ps.meleeWeapon = WeaponId.KNIFE; //    sas.gsc loadout [SAS §5]
    ps.offhandPrimary = WeaponId.HATCHET;
    ps.offHandIndex = at.weaponState >= WeaponState.OFFHAND_INIT && at.weaponState <= WeaponState.OFFHAND_END ? WeaponId.HATCHET : WeaponId.NONE;
    ps.weaponsOwned = (1 << WeaponId.CROSSBOW) | (1 << WeaponId.KNIFE_BALLISTIC) | (1 << WeaponId.HATCHET) | (1 << WeaponId.KNIFE);
    for (let i = 0; i < ps.ammoClip.length; i++) { ps.ammoClip[i] = 1; ps.ammoStock[i] = 1; } // ammo is not networked
    // ADS
    if (at.adsFrac >= 0) p.ads = at.adsFrac;
    else {
      const want = at.moveFlags & MoveFlag.ADS ? 1 : 0;
      const def = at.weapon !== WeaponId.NONE ? getWeaponDef(at.weapon) : null;
      const tt = def ? (want ? def.adsTransInTimeMs : def.adsTransOutTimeMs) : 0;
      const step = tt > 0 ? dtRealMs / tt : 1;
      p.ads = want ? Math.min(1, p.ads + step) : Math.max(0, p.ads - step);
    }
    ps.adsFrac = p.ads;
    // weapon anim
    if (at.weaponAnim >= 0) { ps.weaponAnim = at.weaponAnim; return; }
    for (const e of this.world.frameEvents) {
      if (e.src !== 'pred') continue;
      const anim = animForEvent(e.ev, e.parm, p.ads > 0.5);
      if (anim >= 0) startAnim(ps, anim);
    }
    if (at.weaponState !== p.lastWeaponState) {
      // first replay frame: the event that started the current state is older than the footage
      const anim = p.lastWeaponState < 0 ? animForState(at.weaponState) : animForStateEntry(at.weaponState, p.lastWeaponState);
      if (anim >= 0) startAnim(ps, anim);
      p.lastWeaponState = at.weaponState;
    }
  }
}

/** pm_weapon.ts startWeaponAnim: the toggle bit flips on every start so the viewmodel sees restarts. */
function startAnim(ps: PlayerState, anim: number): void {
  ps.weaponAnim = (anim | ((ps.weaponAnim & WEAPANIM_TOGGLEBIT) ^ WEAPANIM_TOGGLEBIT)) & 0xffff;
}

/** Viewmodel anim started by one of the attacker's networked events (as PM_Weapon starts it), -1 = none. */
export function animForEvent(ev: number, parm: number, ads: boolean): number {
  switch (ev) {
    case PEv.WEAPON_FIRE: return ads ? WeapAnim.ADS_FIRE : WeapAnim.FIRE; //          pm_weapon.ts PM_Weapon_FireWeapon
    case PEv.WEAPON_FIRE_LAST: return ads ? WeapAnim.ADS_LAST_SHOT : WeapAnim.LAST_SHOT;
    case PEv.RELOAD_START: return WeapAnim.RELOAD;
    case PEv.WEAPON_RAISE: {
      const kind = (parm >> 8) & 0xff; // 0 normal 1 first 2 quick 3 empty
      return kind === 1 ? WeapAnim.FIRST_RAISE : kind === 2 ? WeapAnim.QUICK_RAISE : kind === 3 ? WeapAnim.EMPTY_RAISE : WeapAnim.RAISE;
    }
    case PEv.WEAPON_DROP: {
      const kind = (parm >> 8) & 0xff;
      return kind === 2 ? WeapAnim.QUICK_DROP : kind === 3 ? WeapAnim.EMPTY_DROP : WeapAnim.DROP;
    }
    case PEv.MELEE_SWING: return WeapAnim.MELEE;
    case PEv.MELEE_LUNGE: return WeapAnim.MELEE_CHARGE;
    case PEv.OFFHAND_START: return WeapAnim.HOLD_FIRE; //    pm_weapon.ts offHandPrepare (hatchet pull-out)
    case PEv.OFFHAND_END: return WeapAnim.QUICK_RAISE; //    offHandEnd: primary quick raise (empty raise unknown: no ammo)
    default: return -1;
  }
}

/** Anim for entering a weapon state that has no event of its own (sprint, dive, back to idle), -1 = none. */
export function animForStateEntry(state: number, prev: number): number {
  switch (state) {
    case WeaponState.SPRINT_RAISE: return WeapAnim.SPRINT_IN;
    case WeaponState.SPRINT_LOOP: return WeapAnim.SPRINT_LOOP;
    case WeaponState.SPRINT_DROP: return WeapAnim.SPRINT_OUT;
    case WeaponState.DTP_IN: return WeapAnim.DTP_IN;
    case WeaponState.DTP_LOOP: return WeapAnim.DTP_LOOP;
    case WeaponState.DTP_OUT: return WeapAnim.DTP_OUT;
    case WeaponState.OFFHAND_HOLD: return WeapAnim.FIRE; //  offHandStart: the throw (hatchet FIRE anim)
    case WeaponState.MELEE_END: return WeapAnim.QUICK_RAISE; // meleeEnd: crossbow quick raise after a knife swing
    case WeaponState.READY: return prev === WeaponState.READY ? -1 : WeapAnim.IDLE;
    default: return -1;
  }
}

/** Anim for a weapon state the footage starts in (its event was not recorded). */
export function animForState(state: number): number {
  switch (state) {
    case WeaponState.RAISING: case WeaponState.RAISING_ALTSWITCH: return WeapAnim.RAISE;
    case WeaponState.FIRST_RAISE: return WeapAnim.FIRST_RAISE;
    case WeaponState.DROPPING: return WeapAnim.DROP;
    case WeaponState.DROPPING_QUICK: return WeapAnim.QUICK_DROP;
    case WeaponState.RELOADING: case WeaponState.RELOAD_START: case WeaponState.RELOAD_END:
    case WeaponState.RELOADING_INTERUPT: case WeaponState.RELOAD_START_INTERUPT: return WeapAnim.RELOAD;
    case WeaponState.MELEE_INIT: case WeaponState.MELEE_FIRE: return WeapAnim.MELEE;
    case WeaponState.OFFHAND_PREPARE: case WeaponState.OFFHAND_START: return WeapAnim.HOLD_FIRE;
    case WeaponState.OFFHAND_HOLD: case WeaponState.OFFHAND: return WeapAnim.FIRE;
    case WeaponState.OFFHAND_END: case WeaponState.MELEE_END: return WeapAnim.QUICK_RAISE;
    default: { const a = animForStateEntry(state, -1); return a >= 0 ? a : WeapAnim.IDLE; }
  }
}

/** A view of `world` with another match phase (the HUD keeps the FINAL KILLCAM letterbox while a slowed final killcam plays on). */
export function withPhase(world: ClientWorld, phase: MatchPhase): ClientWorld {
  return world.phase === phase ? world : (Object.create(world, { phase: { value: phase } }) as ClientWorld);
}
